import styles from "./Order.module.css";
import { useEffect, useState } from "react";
import { getCart } from "../../utils/cartUtils";
import { setUserLocal, getUserLocal } from "../../utils/userUtils";
import { API_BASE } from "../../config/api";

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  type: string;
}



async function fetchWithAuth(path: string, init: RequestInit = {}) {
  const finalInit: RequestInit = {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  };

  let res = await fetch(API_BASE + path, finalInit);

  if (res.status === 401) {
    const refresh = await fetch(API_BASE + "/api/auth/refresh", {
      method: "POST",
      credentials: "include",
    });

    if (refresh.ok) {
      res = await fetch(API_BASE + path, finalInit);
    }
  }

  return res;
}

export default function Order() {
  const [cartProducts, setCartProducts] = useState<Product[]>([]);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [userId, setUserId] = useState<number | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  /* ─────────────────────────────────────────────
     LOAD CART + USER
  ───────────────────────────────────────────── */
  useEffect(() => {
    const storedIds = getCart();

    fetch(API_BASE + "/api/auth/products", { credentials: "include" })
      .then((res) => res.json())
      .then((data: Product[] | Product) => {
        const products = Array.isArray(data) ? data : [data];
        const selected = products.filter((p) => storedIds.includes(p.id));

        setCartProducts(selected);

        const total = selected.reduce((sum, p) => sum + p.price, 0);
        setTotalPrice(total);
      })
      .catch(() => setError("Failed to load products"));

    const localUser = getUserLocal();
    if (localUser?.id) setUserId(Number(localUser.id));
  }, []);

  /* ─────────────────────────────────────────────
     START CHECKOUT (Stripe)
  ───────────────────────────────────────────── */
  const startCheckout = async () => {
    try {
      const response = await fetchWithAuth(
      "/api/payment/create-checkout-session",
        {
          method: "POST",
          body: JSON.stringify({
            amount: Math.round(totalPrice * 100),
            cart: getCart(),
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to start checkout");

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      setError("Could not start checkout");
    }
  };

  /* ─────────────────────────────────────────────
     LOGIN
  ───────────────────────────────────────────── */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      const res = await fetch(API_BASE + "/api/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) throw new Error("Invalid credentials");

      const data = await res.json();

      setUserLocal(data.user);
      setUserId(Number(data.user.id));

      await startCheckout();
    } catch (err) {
      setError("Login failed");
    }
  };

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <div className={styles.orderContainer}>
      <div className={styles.orderGrid}>
        
        {/* ── LEFT: ORDER SUMMARY ── */}
        <div className={styles.summarySection}>
          <h2>Order Summary</h2>

          {cartProducts.map((p) => (
            <div key={p.id} className={styles.item}>
              <span className={styles.itemName}>{p.name}</span>
              <span className={styles.itemPrice}>
                €{p.price.toFixed(2)}
              </span>
            </div>
          ))}

          <div className={styles.total}>
            <strong>Total</strong>
            <span>€{totalPrice.toFixed(2)}</span>
          </div>

          <p className={styles.taxNote}>
            No taxes will be charged. 
          </p>
        </div>

        {/* ── RIGHT: AUTH / CHECKOUT ── */}
        <div className={styles.authSection}>
          {!userId ? (
            <form className={styles.authForm} onSubmit={handleLogin}>
              <h3>Login to Continue</h3>

              {error && <p className={styles.error}>{error}</p>}

              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              <a href="/resetpassword" className={styles.link}>
                Forgot password?
              </a>

            {  /*    <a href="/signup" className={styles.link}>
                Create account
              </a> */}

              <button type="submit">
                Login & Continue to Payment
              </button>
            </form>
          ) : (
            <div>
              <h3>Ready to Checkout</h3>
              <p>You are logged in. Proceed to secure payment.</p>

              <button onClick={startCheckout}>
                Proceed to Checkout
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}